import { Link, useRouterState } from '@tanstack/react-router';
import { useAuthStore } from '../store/auth.ts';
import { ThemeToggle } from './ThemeToggle.tsx';
import { Icon } from './Icon.tsx';
import { t } from '@/lib/strings';

// Contextual screen title + whether this screen shows a back button (i.e. it's a
// detail/form screen rather than a primary bottom-nav destination). Mirrors
// WapMeet's mobile top app bar.
function screenFor(pathname: string): { title: string; back: boolean } {
  if (pathname === '/') return { title: t.nav.dashboard, back: false };
  if (pathname === '/gsr') return { title: t.nav.gsrs, back: false };
  if (pathname.startsWith('/gsr/')) return { title: t.nav.gsrs, back: true };
  if (pathname === '/vendors') return { title: t.nav.vendors, back: false };
  if (pathname.startsWith('/vendors/')) return { title: t.nav.vendors, back: true };
  if (pathname === '/requests') return { title: t.nav.requests, back: false };
  if (pathname.startsWith('/requests/')) return { title: t.nav.requests, back: true };
  if (pathname.startsWith('/purchase-requests/')) return { title: t.prDetail.heading, back: true };
  if (pathname.startsWith('/payments')) return { title: t.nav.finance, back: false };
  if (pathname.startsWith('/org-settings')) return { title: t.orgSettings.navLabel, back: false };
  return { title: t.app.name, back: false };
}

// Material 3 mobile top app bar: back/logo, contextual title, then the app
// controls. Sign-out and org settings live in the bottom nav on mobile.
export function Header(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { title, back } = screenFor(pathname);

  return (
    <header
      className="md:hidden sticky top-0 z-30 bg-[hsl(var(--chrome))]"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="flex h-14 items-center gap-1 px-1.5">
        {back ? (
          <button
            onClick={() => window.history.back()}
            aria-label={t.common.prev}
            className="grid size-11 place-items-center rounded-full text-foreground active:bg-muted/60"
          >
            {/* RTL: the back arrow points to the inline-start (right). */}
            <Icon name="arrow_forward" size={24} />
          </button>
        ) : (
          <Link to="/" aria-label={t.nav.dashboard} className="grid size-11 place-items-center">
            <img src="/icons/wappro-mark.svg" alt="" className="size-8 object-contain" />
          </Link>
        )}

        <h1 className="flex-1 min-w-0 truncate text-[1.3rem] font-medium text-ink">{title}</h1>

        {user && (
          <div className="flex items-center gap-0.5 shrink-0">
            <ThemeToggle />
          </div>
        )}
      </div>
    </header>
  );
}
